import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {TabelaPrecosService} from '../../produtos-v2/tabela-precos.service';
import {PriceTableTargetTypeV2, ProductPriceTableRowVM} from '../../produtos-v2/produtos-v2.models';
import {NotificationService} from '../../services/notification.service';

interface PriceTableTargetOption {
  tipoAlvo: PriceTableTargetTypeV2;
  alvoId: number;
  nome: string;
  key: string;
}

interface ProductPriceTableTargetRowVM extends ProductPriceTableRowVM {
  target: PriceTableTargetOption;
}

@Component({
  selector: 'app-produto-tabela-precos',
  templateUrl: './produto-tabela-precos.component.html',
  styleUrls: ['./produto-tabela-precos.component.scss']
})
export class ProdutoTabelaPrecosComponent implements OnChanges {
  @Input() empresa: any;
  @Input() produto: any;
  @Input() produtoId: number | null = null;
  @Input() alvoId: number | null = null;
  @Input() tipoAlvo: PriceTableTargetTypeV2 = 'PRODUTO';
  @Input() targetSelecionadoKeyPreferencial: string | null = null;
  @Input() podeEditarPrecos = false;
  @Output() saved = new EventEmitter<void>();

  loading = false;
  loadingTarget = false;
  saving = false;
  linhas: ProductPriceTableTargetRowVM[] = [];
  targetsLista: PriceTableTargetOption[] = [];
  ehProdutoPizzaComTamanhos = false;
  targetSelecionadoKey = '';

  constructor(
    private tabelaPrecosService: TabelaPrecosService,
    private notificationService: NotificationService
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ((changes['empresa'] || changes['produto'] || changes['produtoId'] || changes['alvoId'] || changes['tipoAlvo'] || changes['targetSelecionadoKeyPreferencial'])
      && this.empresa?.catalogo?.id) {
      this.atualizeTargets();
      await this.carregue();
    }
  }

  async carregue(): Promise<void> {
    const target = this.targetSelecionado;
    if (!this.empresa?.catalogo?.id || !target) return;
    const primeiraCarga = !this.linhas.length;
    this.loading = primeiraCarga;
    this.loadingTarget = !primeiraCarga;
    try {
      const linhas = await this.tabelaPrecosService.listeAlvo(
        target.tipoAlvo,
        Number(target.alvoId),
        Number(this.empresa.catalogo.id)
      );
      this.linhas = linhas.map((linha) => ({...linha, target}));
    } catch (e) {
      this.notificationService.showError(e);
    } finally {
      this.loading = false;
      this.loadingTarget = false;
    }
  }

  marqueAlterada(linha: ProductPriceTableTargetRowVM): void {
    linha.dirty = true;
  }

  async salve(): Promise<void> {
    if (!this.empresa?.catalogo?.id || !this.targetsLista.length || !this.podeEditarPrecos) return;

    const alteradas = this.linhas.filter((linha) => linha.dirty);
    if (!alteradas.length) return;
    if (this.existemLinhasAtacadoInvalidas) {
      this.notificationService.showError('Informe quantidade mínima maior que 1 para preços de atacado.');
      return;
    }
    if (this.existemLinhasDescontoInvalidas) {
      this.notificationService.showError(`Informe desconto maior ou igual a zero e menor ou igual ao ${this.rotuloPrecoPrincipal.toLowerCase()}.`);
      return;
    }
    if (this.existemLinhasPrecoInvalidas) {
      this.notificationService.showError(`Informe ${this.rotuloPrecoPrincipal.toLowerCase()} obrigatório e maior ou igual a zero.`);
      return;
    }
    if (this.existemLinhasPrecoAtacadoInvalidas) {
      this.notificationService.showError('Informe preço atacado menor que o preço varejo.');
      return;
    }

    this.saving = true;
    try {
      const grupos = this.agrupeAlteradasPorAlvo(alteradas);
      await Promise.all(Array.from(grupos.values()).map((grupo) =>
        this.tabelaPrecosService.salveAlvo(
          grupo.target.tipoAlvo,
          Number(grupo.target.alvoId),
          Number(this.empresa.catalogo.id),
          grupo.linhas.map((linha) => linha.item)
        )
      ));
      await this.carregue();
      this.notificationService.showSuccess('Tabela de precos salva.');
      this.saved.emit();
    } catch (e) {
      this.notificationService.showError(e);
    } finally {
      this.saving = false;
    }
  }

  get targetId(): number | null {
    return this.alvoId || this.produtoId;
  }

  get linhasExibidas(): ProductPriceTableTargetRowVM[] {
    return this.linhas;
  }

  get targetSelecionado(): PriceTableTargetOption | null {
    return this.targetsLista.find((target) => target.key === this.targetSelecionadoKey) || null;
  }

  get quantidadeAlteracoesPrecos(): number {
    return this.linhas.filter((linha) => linha.dirty).length;
  }

  get exibeColunasAtacado(): boolean {
    return this.linhas.some((linha) => !!linha?.tabela?.suportaPrecosAtacado);
  }

  get existemLinhasAtacadoInvalidas(): boolean {
    return this.linhas.some((linha) => linha.dirty && this.linhaAtacadoInvalida(linha));
  }

  get existemLinhasDescontoInvalidas(): boolean {
    return this.linhas.some((linha) => linha.dirty && this.linhaDescontoInvalida(linha));
  }

  get existemLinhasPrecoInvalidas(): boolean {
    return this.linhas.some((linha) => linha.dirty && this.linhaPrecoInvalida(linha));
  }

  get existemLinhasPrecoAtacadoInvalidas(): boolean {
    return this.linhas.some((linha) => linha.dirty && this.linhaPrecoAtacadoInvalido(linha));
  }

  get podeSalvarPrecos(): boolean {
    if (this.saving || !this.podeEditarPrecos) return false;
    return this.quantidadeAlteracoesPrecos > 0
      && !this.existemLinhasAtacadoInvalidas
      && !this.existemLinhasDescontoInvalidas
      && !this.existemLinhasPrecoInvalidas
      && !this.existemLinhasPrecoAtacadoInvalidas;
  }

  get salvarPrecosLabel(): string {
    const total = this.quantidadeAlteracoesPrecos;
    if (!total) return 'Salvar preços';
    return `Salvar preços (${total})`;
  }

  get produtoVendidoPorPeso(): boolean {
    return this.produto?.tipoDeVenda === 'Peso';
  }

  get unidadePrecoTabela(): string {
    const sigla = String(this.produto?.unidadeMedida?.sigla || 'kg').toLowerCase();
    return sigla === 'g' ? 'kg' : sigla;
  }

  get unidadeQuantidadeTabela(): string {
    return String(this.produto?.unidadeMedida?.sigla || this.unidadePrecoTabela).toLowerCase();
  }

  get formatoPrecoTabela(): string {
    return this.produtoVendidoPorPeso ? `$ 0.00/ ${this.unidadePrecoTabela}` : 'c2';
  }

  get placeholderPrecoTabela(): string {
    return this.produtoVendidoPorPeso ? `R$ 0,00/ ${this.unidadePrecoTabela}` : 'R$ 0,00';
  }

  get sufixoPrecoTabela(): string {
    return this.produtoVendidoPorPeso ? `/${this.unidadePrecoTabela}` : '';
  }

  get rotuloPrecoPrincipal(): string {
    return this.produtoVendidoPorPeso ? `Preço por ${this.unidadePrecoTabela}` : 'Preço unitário';
  }

  get rotuloDesconto(): string {
    return this.produtoVendidoPorPeso ? `Desconto por ${this.unidadePrecoTabela}` : 'Desconto';
  }

  get rotuloPrecoAtacado(): string {
    return this.produtoVendidoPorPeso ? `Preço atacado por ${this.unidadePrecoTabela}` : 'Preço atacado';
  }

  get rotuloQuantidadeMinima(): string {
    return this.produtoVendidoPorPeso ? `Mín. atacado (${this.unidadeQuantidadeTabela})` : 'Qtd. mínima';
  }

  linhaTemDados(linha: ProductPriceTableTargetRowVM): boolean {
    return this.temValor(linha?.item?.valor)
      || Number(linha?.item?.desconto || 0) > 0
      || this.temValor(linha?.item?.valorAtacado);
  }

  linhaTemDesconto(linha: ProductPriceTableTargetRowVM): boolean {
    return Number(linha?.item?.valor || 0) > 0
      && Number(linha?.item?.desconto || 0) > 0
      && !this.linhaDescontoInvalida(linha);
  }

  obtenhaPrecoFinal(linha: ProductPriceTableTargetRowVM): number {
    return Number(linha.item.valor || 0) - Number(linha.item.desconto || 0);
  }

  obtenhaPercentualDesconto(linha: ProductPriceTableTargetRowVM): number {
    const valor = Number(linha.item.valor || 0);
    if (!valor) return 0;
    return (Number(linha.item.desconto || 0) / valor) * 100;
  }

  linhaAtacadoInvalida(linha: ProductPriceTableTargetRowVM): boolean {
    return !!linha?.tabela?.suportaPrecosAtacado
      && this.temValor(linha?.item?.valorAtacado)
      && Number(linha.item.quantidadeMinimaAtacado || 0) <= 1;
  }

  linhaDescontoInvalida(linha: ProductPriceTableTargetRowVM): boolean {
    if (!this.temValor(linha?.item?.desconto)) return false;
    const desconto = Number(linha.item.desconto);
    if (isNaN(desconto) || desconto < 0) return true;
    if (desconto === 0) return false;
    const valor = Number(linha.item.valor);
    return isNaN(valor) || valor <= 0 || desconto > valor;
  }

  linhaPrecoInvalida(linha: ProductPriceTableTargetRowVM): boolean {
    if (!this.temValor(linha?.item?.valor)) return true;
    const valor = Number(linha.item.valor);
    return isNaN(valor) || valor < 0;
  }

  linhaPrecoAtacadoInvalido(linha: ProductPriceTableTargetRowVM): boolean {
    if (!linha?.tabela?.suportaPrecosAtacado || !this.temValor(linha?.item?.valorAtacado)) return false;
    const valorAtacado = Number(linha.item.valorAtacado);
    const valor = Number(linha.item.valor);
    return isNaN(valorAtacado) || valorAtacado < 0 || isNaN(valor) || valorAtacado >= valor;
  }

  canalLabel(canal: string | null): string {
    const labels: any = {
      LOJA: 'Loja',
      DELIVERY: 'Loja',
      RETIRADA: 'Loja',
      MESA: 'Mesa',
      BALCAO: 'Balcao',
      IFOOD: 'iFood',
      '99FOOD': '99Food',
      KEETA: 'Keeta'
    };
    if (!canal) return 'Todos';
    return String(canal)
      .split(',')
      .map((item) => item.trim())
      .filter((item) => !!item)
      .map((item) => labels[item] || item)
      .join(', ') || 'Todos';
  }

  trackByLinha = (index: number, linha: ProductPriceTableTargetRowVM): string =>
    `${linha.target.key}:${linha.tabela?.id || index}`;

  async switchTarget(targetKey: string): Promise<void> {
    if (this.targetSelecionadoKey === targetKey || this.loading || this.loadingTarget) return;
    this.targetSelecionadoKey = targetKey;
    await this.carregue();
  }

  private temValor(valor: any): boolean {
    return valor !== null && valor !== undefined && valor !== '';
  }

  private atualizeTargets(): void {
    this.ehProdutoPizzaComTamanhos = this.tipoAlvo === 'PRODUTO'
      && this.produto?.tipo === 'pizza'
      && Array.isArray(this.produto?.tamanhos)
      && this.produto.tamanhos.some((tamanho: any) => !!tamanho?.id);

    if (this.ehProdutoPizzaComTamanhos) {
      this.targetsLista = (this.produto.tamanhos || [])
        .filter((tamanho: any) => !!tamanho?.id)
        .map((tamanho: any, index: number) => ({
          tipoAlvo: 'PRODUTO_TAMANHO' as PriceTableTargetTypeV2,
          alvoId: Number(tamanho.id),
          nome: tamanho.descricao || tamanho?.template?.descricao || tamanho?.template?.nome || `Tamanho ${index + 1}`,
          key: `PRODUTO_TAMANHO:${Number(tamanho.id)}`
        }));
    } else if (this.targetId) {
      const tipoAlvo = this.tipoAlvo;
      const alvoId = Number(this.targetId);
      this.targetsLista = [{
        tipoAlvo,
        alvoId,
        nome: 'Produto',
        key: `${tipoAlvo}:${alvoId}`
      }];
    } else {
      this.targetsLista = [];
    }

    if (this.targetSelecionadoKeyPreferencial
      && this.targetsLista.some((target) => target.key === this.targetSelecionadoKeyPreferencial)) {
      this.targetSelecionadoKey = this.targetSelecionadoKeyPreferencial;
      return;
    }

    if (!this.targetSelecionadoKey || !this.targetsLista.some((target) => target.key === this.targetSelecionadoKey)) {
      this.targetSelecionadoKey = this.targetsLista[0]?.key || '';
    }
  }

  private agrupeAlteradasPorAlvo(linhas: ProductPriceTableTargetRowVM[]): Map<string,
    { target: PriceTableTargetOption; linhas: ProductPriceTableTargetRowVM[] }> {
    const grupos = new Map<string, { target: PriceTableTargetOption; linhas: ProductPriceTableTargetRowVM[] }>();
    linhas.forEach((linha) => {
      const key = linha.target.key;
      if (!grupos.has(key)) grupos.set(key, {target: linha.target, linhas: []});
      grupos.get(key).linhas.push(linha);
    });
    return grupos;
  }
}
